import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Search, Calendar, User, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerOffresArchivees = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [archivedOffers, setArchivedOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchArchivedOffers();
  }, []);

  useEffect(() => {
    filterOffers();
  }, [archivedOffers, searchQuery]);

  const fetchArchivedOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        // Filter archived offers for current user's company
        const archived = offers.filter(offer => 
          offer.status === 'archive' && offer.company === user.company
        );
        setArchivedOffers(archived);
      } else {
        toast.error('Erreur lors du chargement des offres archivées');
      }
    } catch (error) {
      console.error('Error fetching archived offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const filterOffers = () => {
    if (!searchQuery.trim()) {
      setFilteredOffers(archivedOffers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = archivedOffers.filter(offer =>
      offer.title.toLowerCase().includes(query) ||
      offer.company.toLowerCase().includes(query) ||
      (offer.creator_name && offer.creator_name.toLowerCase().includes(query))
    );
    setFilteredOffers(filtered);
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToManager = () => {
    if (user.company === 'Horizon Solution') {
      navigate('/manager-horizon');
    } else {
      navigate('/manager');
    }
  };

  const handleOfferClick = (offer) => {
    navigate(`/manager/archived-offer-detail/${offer.id}`);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="manager-offres-archivees-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToManager}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="rh-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1>Offres archivées - {user?.company}</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Search Bar */}
        <div className="search-section">
          <div className="search-bar">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Rechercher par intitulé, entreprise ou manager..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="search-counter">
            {filteredOffers.length} offre{filteredOffers.length !== 1 ? 's' : ''} archivée{filteredOffers.length !== 1 ? 's' : ''}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="recruitment-grid-compact">
            {filteredOffers.map((offer) => (
              <Card 
                key={offer.id}
                className="recruitment-card-compact cursor-pointer group"
                onClick={() => handleOfferClick(offer)}
              >
                <CardContent className="recruitment-card-compact-content">
                  <div className="recruitment-card-compact-header">
                    <img
                      src={companyLogos[offer.company]}
                      alt={`Logo ${offer.company}`}
                      className="recruitment-card-compact-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Eye className="recruitment-card-compact-icon" />
                  </div>
                  <h3 className="recruitment-card-compact-title">
                    {offer.company} : {offer.title}
                  </h3>
                  <div className="recruitment-card-compact-info">
                    <div className="recruitment-card-compact-info-item">
                      <User size={14} />
                      <span><strong>Manager :</strong> {offer.creator_name || 'Non spécifié'}</span>
                    </div>
                    <div className="recruitment-card-compact-info-item">
                      <Calendar size={14} />
                      <span><strong>Date idéale :</strong> {formatDate(offer.date_arrivee)}</span>
                    </div>
                    <div className="recruitment-card-compact-info-item archive-date-item">
                      <Calendar size={14} />
                      <span><strong>Archivée le :</strong> {formatDate(offer.updated_at)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredOffers.length === 0 && !loading && (
              <div className="empty-state-compact">
                <p>
                  {searchQuery 
                    ? 'Aucune offre archivée ne correspond à votre recherche' 
                    : 'Aucune offre archivée'
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ManagerOffresArchivees;